import React, { useEffect, useState } from 'react';
import { Spinner } from 'reactstrap';
import PropTypes from 'prop-types';
import { useParams, useHistory } from 'react-router-dom';
import { gql } from '@apollo/client';
import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import { get, isEmpty, getOr } from 'lodash/fp';
import {
  FETCH_EVENT_HEADER,
  FETCH_REGISTRATION_OPTION_DETAIL,
  FETCH_ORDER_DETAIL,
  FETCH_REGION_DETAIL,
  FETCH_TENANT_DETAIL,
} from '../../GraphQL/Queries';
import {
  CHECK_COUPON_VALIDITY,
  UPDATE_ORDER,
} from '../../GraphQL/Mutations';
import useFormParams from '../../Hooks/useFormParams';
import useCurrentUser from '../../Hooks/userCurrentUser';
import { Input } from '../../Components/Form';
import {
  LinkText,
  PaymentDescription,
  CircleIcon,
  PrimaryButton,
} from '../../Components/Auth/Layout';
import useHeaderComponentsMutation from '../../Hooks/useHeaderComponentsMutation';
import useGraphQLErrorExtractor from '../../Hooks/useGraphQLErrorExtractor';
import GenericAlert from '../../Components/GenericAlert';
import CIRCLE_ARROW from '../../Images/icons/circle-arrow.png';
import BackLink from '../../Components/BackLink';
import Loader from '../../Components/Loader';
import useUpdateOrdersCache from '../../Hooks/useUpdateOrdersCache';
import { EVENT_TITLE } from '../../Constants/AppConstants';
import JoinEventSlider from './JoinEventSlider';
import { CartDonationSection } from './CartDonationSection';

const DONATE_AMOUNT = gql`
  mutation donateAmount($orderId: Int!, $amount: Int!) {
    donateAmount(
      input: {
        orderId: $orderId,
        amount: $amount
      }
    ) {
      order {
        id
        status
        amount
        mistFee
        stripeFee
        fetchLineItems {
          id
          amount
          description
          lineItemAbleId
          lineItemAbleType
        }
      }
    }
  }
`;

const LineItemPropType = PropTypes.shape({
  lineItemAbleId: PropTypes.number.isRequired,
  lineItemAbleType: PropTypes.string.isRequired,
  amount: PropTypes.number.isRequired,
  description: PropTypes.string.isRequired,
});

const CouponLineItem = ({ lineItem }) => (
  <>
    <p>
      Coupon Discount
      <span style={{ color: '#F4AB37', float: 'right' }}>
          $
        {parseFloat(lineItem.amount / 100).toFixed(2)}
      </span>
    </p>
    <hr />
  </>
);
CouponLineItem.propTypes = {
  lineItem: LineItemPropType.isRequired,
};

const MerchandiseLineItem = ({ lineItem }) => (
  <>
    <p>
      Add-on:
      {' '}
      {lineItem.description}
      <span style={{ color: '#F4AB37', float: 'right' }}>
          $
        {parseFloat(lineItem.amount / 100).toFixed(2)}
      </span>
    </p>
    <hr />
  </>
);
MerchandiseLineItem.propTypes = {
  lineItem: LineItemPropType.isRequired,
};

const RegistrationOptionLineItem = ({ lineItem, event }) => {
  const option = event.fetchRegistrationOptions
    .find((ro) => ro.id === lineItem.lineItemAbleId.toString());
  return (
    <>
      <p>
        Registration type:
        {' '}
        {get('title', option)}
        <span style={{ color: '#F4AB37', float: 'right' }}>
          $
          {parseFloat(lineItem.amount / 100).toFixed(2)}
        </span>
      </p>
      <hr />
    </>
  );
};
RegistrationOptionLineItem.propTypes = {
  lineItem: LineItemPropType.isRequired,
  event: PropTypes.shape({
    fetchRegistrationOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
  }).isRequired,
};

const DonationLineItem = ({ lineItem }) => (
  <>
    <p>
        Donation:
      {' '}
      <span style={{ color: '#F4AB37', float: 'right' }}>
          $
        {parseFloat(lineItem.amount / 100).toFixed(2)}
      </span>
    </p>
    <hr />
  </>
);
DonationLineItem.propTypes = {
  lineItem: LineItemPropType.isRequired,
  event: PropTypes.shape({
    fetchRegistrationOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
  }).isRequired,
};

export const LineItem = ({ lineItem, event }) => {
  switch (lineItem.lineItemAbleType) {
    case 'Coupon': return <CouponLineItem lineItem={lineItem} />;
    case 'Merchandise': return <MerchandiseLineItem lineItem={lineItem} />;
    case 'RegistrationOption': return <RegistrationOptionLineItem lineItem={lineItem} event={event} />;
    case 'Donation': return <DonationLineItem lineItem={lineItem} />;
    default: return null;
  }
};
LineItem.propTypes = {
  lineItem: LineItemPropType.isRequired,
  event: PropTypes.shape({
    fetchRegistrationOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
  }).isRequired,
};

function getOrderAmount(order, donation) {
  if (!order.fetchLineItems) {
    return parseFloat(0).toFixed(2);
  }

  let amount = get('amount', order);

  const donationLineItem = order.fetchLineItems.filter(
    ({ lineItemAbleType }) => lineItemAbleType === 'Donation',
  )[0];
  if (donationLineItem) {
    // there's already a donation on the order
    // remove the amount
    // we'll add the "current" donation value in the next step
    amount -= donationLineItem.amount;
  }
  if (donation) {
    amount += donation;
  }

  return parseFloat(amount / 100, 10).toFixed(2);
}

const JoinEventCart = () => {
  const {
    currentUser,
  } = useCurrentUser(true);

  const { orderId } = useParams();
  const [order, setOrder] = useState({});
  const [totalAmount, setTotalAmount] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  // set once when we fetch order
  const [orderDonationAmount, setOrderDonationAmount] = useState(0);
  // updates to reflect ui

  const [donationAmountCents, setDonationAmountCents] = useState(0);
  const donationAmount = donationAmountCents / 100;
  const setDonationAmount = (a) => setDonationAmountCents(a * 100);
  const extractError = useGraphQLErrorExtractor();

  const history = useHistory();
  const { params, onChange, setParams } = useFormParams({
    couponCode: '',
  });

  const getLineItemsTotal = (orderObj) => orderObj.fetchLineItems.filter(
    ({ lineItemAbleType }) => lineItemAbleType === 'RegistrationOption',
  ).map(
    (item) => item.amount,
  ).reduce((prev, next) => prev + next);

  const [fetchTenantDetail] = useLazyQuery(
    FETCH_TENANT_DETAIL, {
      onCompleted: (dataTenantDetail) => {
        const tempMissFee = (dataTenantDetail.fetchTenantDetail.percentageShare / 100);
        let intermediateSum = totalAmount;
        intermediateSum += (order.fetchLineItems.filter(
          ({ lineItemAbleType }) => lineItemAbleType === 'RegistrationOption',
        )[0].amount * tempMissFee);
        setTotalAmount(intermediateSum);
      },
    },
  );

  const [fetchRegionDetail] = useLazyQuery(
    FETCH_REGION_DETAIL, {
      onCompleted: (dataRegionDetail) => {
        fetchTenantDetail({
          variables: {
            id: parseInt(dataRegionDetail.fetchRegionDetail.tenantId, 10),
          },
        });
      },
    },
  );

  const [fetchEventHeader, {
    data: dataFetchEventHeader,
    loading: eventLoading,
  }] = useLazyQuery(FETCH_EVENT_HEADER);

  const event = getOr({}, 'fetchEventDetail', dataFetchEventHeader);

  useEffect(() => {
    if (!isEmpty(event)) {
      fetchRegionDetail({
        variables: {
          id: parseInt(event.regionId, 10),
        },
      });
    }
  }, [fetchRegionDetail, event]);

  const updateOrdersCache = useUpdateOrdersCache(getOr(0, 'id', currentUser), getOr(0, 'id', event));


  const [fetchRegistrationOptionDetail, { data: dataRegistrationOption }] = useLazyQuery(
    FETCH_REGISTRATION_OPTION_DETAIL,
  );

  const option = getOr({}, 'fetchRegistrationOptionDetail', dataRegistrationOption);

  const [fetchOrderDetail, {
    data: dataFetchOrderDetail,
    loading: orderLoading,
  }] = useLazyQuery(FETCH_ORDER_DETAIL);

  useEffect(() => {
    if (dataFetchOrderDetail?.fetchOrderDetail && order !== {}) {
      const orderData = dataFetchOrderDetail.fetchOrderDetail;
      setOrder(orderData);
      let totalStart = getLineItemsTotal(orderData);
      const lineItemCouponObj = orderData.fetchLineItems.filter(
        ({ lineItemAbleType }) => lineItemAbleType === 'Coupon',
      )[0];
      totalStart += lineItemCouponObj ? lineItemCouponObj.amount : 0;
      setTotalAmount(totalStart);
      fetchEventHeader({
        variables: {
          id: parseInt(orderData.eventId, 10),
        },
      });
      fetchRegistrationOptionDetail({
        variables: {
          id: parseInt(orderData.fetchLineItems[0].lineItemAbleId, 10),
        },
      });

      const donationLineItem = orderData.fetchLineItems.filter(
        ({ lineItemAbleType }) => lineItemAbleType === 'Donation',
      )[0];
      if (donationLineItem) {
        setDonationAmountCents(donationLineItem.amount);
        setOrderDonationAmount(donationLineItem.amount);
      }
    }
  }, [dataFetchOrderDetail, fetchEventHeader, fetchRegistrationOptionDetail, order]);

  const [checkCouponValidity, { error, loading }] = useMutation(
    CHECK_COUPON_VALIDITY, {
      onCompleted: (data) => {
        if (data.checkCouponValidity.message) setErrorMessage(data.checkCouponValidity.message);
        else {
          setErrorMessage('');
          setOrder(data.checkCouponValidity.order);
          setTotalAmount(totalAmount + data.checkCouponValidity.lineItem.amount);
          setParams({
            couponCode: '',
          });
        }
      },
      onError: (e) => {
        console.log(e);
        setErrorMessage(extractError(e));
      },
    },
  );

  const [updateOrder, { loading: updateLoading }] = useMutation(
    UPDATE_ORDER, {
      onCompleted: (data) => {
        updateOrdersCache(data.updateOrder.order, 'update');
        history.push(`/payment-confirmation/${orderId}`);
      },
    },
  );

  useHeaderComponentsMutation({
    title: 'CHECKOUT',
    components: [{ key: EVENT_TITLE, value: get('title', event) }],
  });


  useEffect(() => {
    if (currentUser && orderId) {
      fetchOrderDetail({
        variables: {
          id: parseInt(orderId, 10),
        },
      });
    }
  }, [currentUser, fetchOrderDetail, orderId]);


  const couponValidityCheck = () => {
    if (!isEmpty(params.couponCode)) {
      return checkCouponValidity({
        variables: {
          orderId: parseInt(order.id, 10),
          registrationOptionId: params.registrationOptionId
            ? parseInt(params.registrationOptionId, 10)
            : order.fetchLineItems[0].lineItemAbleId,
          couponCode: params.couponCode,
        },
      });
    }
    return Promise.resolve();
  };

  const [donateAmount, { loading: donationLoading }] = useMutation(DONATE_AMOUNT, {
    onCompleted: (data) => {
      updateOrdersCache(data.donateAmount.order, 'update');
    },
  });

  const onContinue = async (amount) => {
    if (!isEmpty(params.couponCode)) {
      const r = await couponValidityCheck();
      if (!r) {
        return;
      }
    }

    if (amount === 0) {
      updateOrder({
        variables: {
          id: parseInt(order.id, 10),
          status: 2,
          transactionId: 'FREEREGISTRATION',
        },
      });
    } else {
      history.push(`/order/${orderId}/billing`);
    }
  };
  const onSave = () => {
    if (donationAmountCents !== orderDonationAmount) {
      if (Number.isNaN(donationAmount)) {
        setErrorMessage('Please supply a valid donation amount');
        return;
      }

      // they don't match, apply the donation and then move on
      donateAmount({
        variables: {
          orderId: parseInt(orderId, 10),
          amount: donationAmountCents ?? 0,
        },
      }).then((res) => {
        onContinue(res.data.donateAmount.order.amount);
      }).catch((e) => {
        setErrorMessage(extractError(e));
      });
    } else {
      onContinue(get('amount', order));
    }
  };

  if (loading || eventLoading || orderLoading || updateLoading) {
    return <Loader />;
  }

  const hasCoupon = order && order.fetchLineItems && order.fetchLineItems.filter(
    ({ lineItemAbleType }) => lineItemAbleType === 'Coupon',
  ).length > 0;

  return (
    <>
      <div className="row">
        <div className="col-12 mx-auto px-4 pt-3">
          {error
            ? <GenericAlert>{extractError(error)}</GenericAlert>
            : (
              <>
                {errorMessage
                  ? <GenericAlert>{errorMessage}</GenericAlert>
                  : null}
              </>
            )}
          <JoinEventSlider
            event={event}
            order={order}
            selectedRegistrationOption={option}
          />
          <p style={{ fontWeight: '600' }}>Your Cart</p>
          <div>
            <div style={{ margin: 'auto', textAlign: 'center' }} />
            <hr />
            <LinkText color="#000" style={{ display: 'block' }}>
              <p>
                {get('firstname', currentUser)}
                {' '}
                {get('lastname', currentUser)}
              </p>
              <p>{event.title}</p>
              <hr />
            </LinkText>
            <PaymentDescription className="pt-3">
              {order && order.fetchLineItems
                && order.fetchLineItems.sort((li1, li2) => li1.id - li2.id).map((lineItem) => (
                  <LineItem key={lineItem.id} lineItem={lineItem} event={event} />
                ))}
            </PaymentDescription>
            {!hasCoupon && (
              <form onSubmit={couponValidityCheck}>
                <Input
                  title="Coupon Code"
                  params={params}
                  withFormGroup
                  id="couponCode"
                  name="couponCode"
                  placeholder="Coupon Code"
                  onChange={onChange}
                  setParams={setParams}
                />
                <CircleIcon onClick={couponValidityCheck}>
                  <span><img src={CIRCLE_ARROW} alt="Apply Coupon" /></span>
                </CircleIcon>
                <hr />
              </form>
            )}

            <CartDonationSection
              donationAmount={donationAmount}
              setDonationAmount={setDonationAmount}
            />

            <PaymentDescription>
              <hr />
              <p style={{ textAlign: 'center' }}>
                Total
                <span style={{ textAlign: 'center', float: 'inherit' }}>
                  {' '}
                    $
                  {getOrderAmount(order, donationAmountCents)}
                </span>
                {` (${event.currency})`}
              </p>
            </PaymentDescription>
            <hr />
            <PrimaryButton
              backgroundcolor="#F4AB37"
              fontColor="#FFF"
              border="2px solid #FFF"
              marginbottom="-2rem"
              onClick={onSave}
              disabled={donationLoading}
              type="button"
            >
              {donationLoading ? (<Spinner />) : 'AGREE AND SUBMIT'}
            </PrimaryButton>
            <div className="pt-3" />
          </div>
          <BackLink to={`/event-manage/${order.eventId}`} imageNotRequired>
                Skip For Now
          </BackLink>
        </div>
      </div>
    </>
  );
};

export default JoinEventCart;
